import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { isFriendNotice } from './types'
import { PushNotifications } from '@capacitor/push-notifications'
import { api, readSession } from './api'
import { callNotification, queueCallNotification } from './callNotification'

/**
 * Capacitor 的 APNs 入口。只在带 PushNotifications 插件的新原生包里运行；
 * 旧审核包仍会加载同一线上网页，所以必须做插件版本检测。
 */
const PREFERENCE_KEY = 'shixu.nativePush.enabled.v1'
const TOKEN_TIMEOUT_MS = 25_000

export type NativePushResult = { ok: boolean; reason?: string; devices?: number; nativeEnabled?: boolean }
export type NativePermission = NotificationPermission

let permission: NativePermission = 'default'
let activeSessionToken: string | null = null
let deviceToken: string | null = null
let registeredSessionToken: string | null = null
let serviceEnabled = false
let registeredDevices = 0
let listenersReady: Promise<void> | null = null
type PushOperation = { id: number; sessionToken: string; cancelled: Promise<void>; aborted: boolean; cancel: () => void }
let nextOperationId = 0
let currentOperation: PushOperation | null = null
let activeOperation: PushOperation | null = null
let nativeMutations: Promise<void> = Promise.resolve()
let registration: { operation: PushOperation; promise: Promise<NativePushResult> } | null = null
let waiting: { operation: PushOperation; resolve: (value: NativePushResult) => void; reject: (reason: Error) => void } | null = null
let binding: { operation: PushOperation; token: string; promise: Promise<void> } | null = null
const progressListeners = new Set<(message: string) => void>()
let registrationProgress = ''

function reportProgress(message: string) {
  registrationProgress = message
  progressListeners.forEach(listener => listener(message))
}

export function watchNativePushProgress(listener: (message: string) => void): () => void {
  progressListeners.add(listener)
  listener(registrationProgress)
  return () => { progressListeners.delete(listener) }
}

export function nativePushAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications')
}

export function nativePushEnabled(): boolean {
  try { return localStorage.getItem(PREFERENCE_KEY) !== 'off' } catch { return true }
}

export function nativePushPermission(): NativePermission { return permission }
export function nativePushRegistered(): boolean { return Boolean(registeredSessionToken && registeredSessionToken === readSession()?.token) }
export function nativePushReady(): boolean { return nativePushRegistered() && permission === 'granted' && serviceEnabled }
export function noteNativePushService(enabled: boolean): void { serviceEnabled = enabled }

function setEnabled(enabled: boolean) {
  try { localStorage.setItem(PREFERENCE_KEY, enabled ? 'on' : 'off') } catch { /* 存储不可用时仍保留本次操作 */ }
}

// Capacitor 插件代理会为任意属性创建方法（包括 then）。不能从 async 函数
// 返回该代理：Promise 会把它当作 thenable，调用不存在的 then 并永远等待。
// 直接引用官方导出，组件与启动脚本一起加载，不再额外请求懒加载文件。

export async function refreshNativePushPermission(): Promise<NativePermission> {
  if (!nativePushAvailable()) return 'default'
  const operation = currentOperation
  try {
    const status = await withTimeout(PushNotifications.checkPermissions(), 5_000, '读取系统通知权限超时')
    if (currentOperation !== operation) return permission
    permission = status.receive === 'granted' ? 'granted' : status.receive === 'denied' ? 'denied' : 'default'
    if (permission !== 'granted') registeredSessionToken = null
  } catch { if (currentOperation === operation) permission = 'default' }
  return permission
}

function completeWaiting(operation: PushOperation, result: NativePushResult) {
  if (waiting?.operation !== operation) return
  waiting.resolve(result)
  waiting = null
}

function failWaiting(operation: PushOperation, error: Error) {
  if (waiting?.operation !== operation) return
  waiting.reject(error)
  waiting = null
}

function cancelledError() { return new Error('通知操作已取消，登录状态或通知设置已变化') }

function beginOperation(sessionToken: string): PushOperation {
  if (currentOperation) {
    currentOperation.cancel()
    failWaiting(currentOperation, cancelledError())
  }
  let resolveCancellation!: () => void
  const cancelled = new Promise<void>(resolve => { resolveCancellation = resolve })
  const operation: PushOperation = { id: ++nextOperationId, sessionToken, cancelled, aborted: false, cancel: () => { operation.aborted = true; resolveCancellation() } }
  currentOperation = operation
  activeOperation = null
  return operation
}

function registrationCurrent(operation: PushOperation): boolean {
  return currentOperation === operation && !operation.aborted && readSession()?.token === operation.sessionToken
}

function requireCurrent(operation: PushOperation) {
  if (!registrationCurrent(operation)) throw cancelledError()
}

function operationWait<T>(operation: PushOperation, task: Promise<T>, ms: number, message: string): Promise<T> {
  return withTimeout(Promise.race([task, operation.cancelled.then(() => { throw cancelledError() })]), ms, message)
}

// APNs registration is device-wide. An old unregister must finish before a new register
// reaches the native bridge, even when an account changes while its server request is slow.
function mutateNative(task: () => Promise<void>): Promise<void> {
  const result = nativeMutations.then(task, task)
  nativeMutations = result.catch(() => {})
  return result
}

function repairServerBinding(operation: PushOperation, token: string): void {
  if (activeOperation !== operation || !registrationCurrent(operation) || deviceToken !== token || permission !== 'granted') return
  const request = api.nativePushRegister(operation.sessionToken, token)
  void request.then(() => {
    if (activeOperation !== operation || !registrationCurrent(operation) || deviceToken !== token) {
      void withTimeout(removeServerBinding(operation, token, true), 5_000, '清理旧设备关联超时').catch(() => {})
    }
  }).catch(() => {})
  void withTimeout(request, 8_000, '恢复当前设备关联超时').catch(() => {})
}

function removeServerBinding(operation: PushOperation, token: string, restoreAnyAccount = false) {
  const request = api.nativePushUnregister(operation.sessionToken, token)
  const reconcile = () => {
    const latest = activeOperation, latestToken = deviceToken
    if (latest && latest !== operation && registrationCurrent(latest) && latestToken && (restoreAnyAccount || latest.sessionToken === operation.sessionToken)) {
      repairServerBinding(latest, latestToken)
    }
  }
  // The deletion can finish after a new registration, including after our timeout.
  // Restore the latest intent even if it is still awaiting its first HTTP result.
  void request.then(reconcile, reconcile).catch(() => {})
  return request
}

async function handleToken(value: string) {
  const token = value.trim().toLowerCase()
  const operation = activeOperation
  if (!operation || !registrationCurrent(operation) || permission !== 'granted') return
  if (!/^(?:[a-f0-9]{2}){16,128}$/.test(token)) {
    failWaiting(operation, new Error('设备推送令牌无效'))
    return
  }
  if (binding?.operation === operation && binding.token === token) return binding.promise
  deviceToken = token
  const task: Promise<void> = (async () => {
    try {
      reportProgress('已取得 Apple 设备令牌，正在登记到诗序…')
      const request = api.nativePushRegister(operation.sessionToken, token)
      // A timed-out HTTP request can still finish. Remove only its old account/session
      // binding; never clear native state or the current account's registration here.
      void request.then(() => {
        if (!registrationCurrent(operation) || activeOperation !== operation) {
          const latest = activeOperation, latestToken = deviceToken
          if (latest && registrationCurrent(latest) && latest.sessionToken === operation.sessionToken && latestToken) {
            if (latestToken !== token) repairServerBinding(latest, latestToken)
          } else {
            void withTimeout(removeServerBinding(operation, token, true), 5_000, '清理旧设备关联超时').catch(() => {})
          }
        }
      }).catch(() => {})
      const result = await operationWait(operation, request, 8_000, '诗序通知登记超时，请检查网络后重试')
      requireCurrent(operation)
      registeredSessionToken = operation.sessionToken
      registeredDevices = result.nativeDevices
      if (typeof result.nativeEnabled === 'boolean') serviceEnabled = result.nativeEnabled
      completeWaiting(operation, { ok: true, devices: result.nativeDevices, nativeEnabled: serviceEnabled })
      reportProgress('设备登记完成')
    } catch (error) {
      failWaiting(operation, error instanceof Error ? error : new Error('服务器登记推送失败'))
    } finally {
      if (binding?.operation === operation && binding.token === token) binding = null
    }
  })()
  binding = { operation, token, promise: task }
  return task
}

async function ensureListeners() {
  if (listenersReady) return listenersReady
  listenersReady = (async () => {
    const native = PushNotifications
    const handles: PluginListenerHandle[] = []
    try {
    handles.push(await native.addListener('registration', ({ value }) => { void handleToken(value) }))
    handles.push(await native.addListener('registrationError', ({ error }) => {
      if (activeOperation && registrationCurrent(activeOperation)) failWaiting(activeOperation, new Error(String(error || 'APNs 设备登记失败')))
    }))
    handles.push(await native.addListener('pushNotificationReceived', (notification) => {
      const call = callNotification(notification.data)
      if (call) {
        window.dispatchEvent(new CustomEvent('shixu:open-call', { detail: call }))
        return
      }
      const messageId = typeof notification.data?.messageId === 'string' ? notification.data.messageId : ''
      const recipientId = typeof notification.data?.recipientId === 'string' ? notification.data.recipientId : ''
      if (messageId) window.dispatchEvent(new CustomEvent('shixu:native-push-received', { detail: { messageId, recipientId } }))
      else window.dispatchEvent(new CustomEvent('shixu:native-push-alert', { detail: {
        id: notification.id,
        title: notification.title,
        body: notification.body,
        recipientId,
        friendNotice: notification.data?.friendNotice,
        readerUrl: notification.data?.url,
      } }))
    }))
    handles.push(await native.addListener('pushNotificationActionPerformed', (action) => {
      const call = queueCallNotification(action.notification?.data)
      if (call) {
        window.dispatchEvent(new CustomEvent('shixu:open-call', { detail: call }))
        window.dispatchEvent(new Event('shixu:open-messages'))
        return
      }
      const notice = action.notification?.data?.friendNotice
      const recipientId = action.notification?.data?.recipientId
      if (isFriendNotice(notice) && notice.kind === 'request') {
        window.dispatchEvent(new CustomEvent('shixu:open-friend-requests', { detail: { recipientId } }))
      } else if(typeof action.notification?.data?.url==='string' && /[?&](readerRoom|readingReminder)=/.test(action.notification.data.url)) {
        window.dispatchEvent(new CustomEvent('shixu:open-reading',{detail:{url:action.notification.data.url,recipientId}}))
      } else window.dispatchEvent(new Event('shixu:open-messages'))
    }))
    } catch (error) {
      await Promise.allSettled(handles.map(handle => handle.remove()))
      throw error
    }
  })()
  try { await listenersReady } catch (error) { listenersReady = null; throw error }
}

/** 页面启动即安装前台 APNs 监听器；不依赖登录状态或服务端网络。 */
export async function prepareNativePushListeners(): Promise<void> {
  if (nativePushAvailable()) await ensureListeners()
}

function withTimeout<T>(task: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  return Promise.race([task, new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error(message)), ms) })])
    .finally(() => { if (timer) clearTimeout(timer) })
}

/** 用户点击「开启」时申请 iOS 权限，并把 APNs token 交给本账号的服务端。 */
export async function registerNativePush(sessionToken: string, askPermission = true): Promise<NativePushResult> {
  if (!nativePushAvailable()) return { ok: false, reason: '当前 App 版本尚未包含原生通知，请更新诗序' }
  if (readSession()?.token !== sessionToken) return { ok: false, reason: '登录状态已变化，请重新开启通知' }
  if (registration?.operation.sessionToken === sessionToken && currentOperation === registration.operation) return registration.promise
  const operation = beginOperation(sessionToken)
  activeSessionToken = sessionToken
  registeredSessionToken = null
  registeredDevices = 0
  const task = (async (): Promise<NativePushResult> => {
    try {
      const native = PushNotifications
      reportProgress('正在读取系统通知权限…')
      let status = await operationWait(operation, native.checkPermissions(), 5_000, '读取系统通知权限超时，请重新打开诗序后重试')
      requireCurrent(operation)
      permission = status.receive === 'granted' ? 'granted' : status.receive === 'denied' ? 'denied' : 'default'
      if (permission !== 'granted') registeredSessionToken = null
      if (status.receive === 'prompt' && askPermission) {
        reportProgress('请在系统弹窗中允许诗序通知')
        status = await operationWait(operation, native.requestPermissions(), 60_000, '系统通知权限尚未确认，请允许通知后重新开启')
        requireCurrent(operation)
        permission = status.receive === 'granted' ? 'granted' : status.receive === 'denied' ? 'denied' : 'default'
      }
      if (status.receive !== 'granted') return { ok: false, reason: status.receive === 'denied' ? '通知权限已关闭，请在 iPhone 设置中允许诗序通知' : '请点击「开启消息通知」并允许系统通知' }
      await operationWait(operation, ensureListeners(), 10_000, '加载原生通知组件超时')
      requireCurrent(operation)
      reportProgress('系统权限已允许，正在连接 Apple 推送服务…')
      const received = new Promise<NativePushResult>((resolve, reject) => { waiting = { operation, resolve, reject } })
      // 监听结果从请求发起时就开始等待，避免 register/error 回调先于 Promise 安装处理器。
      const [, result] = await Promise.all([
        operationWait(operation, mutateNative(async () => {
          requireCurrent(operation)
          activeOperation = operation
          await native.register()
        }), 15_000, '原生登记操作未响应，请完全关闭并重新打开诗序后重试'),
        operationWait(operation, received, TOKEN_TIMEOUT_MS, '等待 APNs 设备令牌超时，请检查网络后重试'),
      ])
      requireCurrent(operation)
      if (result.ok) setEnabled(true)
      return result
    } catch (error) {
      operation.cancel()
      failWaiting(operation, error instanceof Error ? error : new Error('原生推送登记失败'))
      if (activeOperation === operation) activeOperation = null
      if (currentOperation === operation) registeredSessionToken = null
      return { ok: false, reason: error instanceof Error ? error.message : '原生推送登记失败' }
    } finally {
      if (waiting?.operation === operation) waiting = null
      if (currentOperation === operation) reportProgress('')
    }
  })()
  registration = { operation, promise: task }
  try { return await task } finally { if (registration?.promise === task) registration = null }
}

/** 回前台重新读取系统权限；权限仍允许时沿用登记并有界核对服务状态。 */
export async function restoreNativePush(sessionToken: string): Promise<NativePushResult> {
  if (!nativePushEnabled()) return { ok: false, reason: '本设备的通知已关闭' }
  if (readSession()?.token !== sessionToken) return { ok: false, reason: '登录状态已变化，请重新开启通知' }
  if (registration?.operation.sessionToken === sessionToken && currentOperation === registration.operation) return registration.promise
  const operation = currentOperation
  const status = await refreshNativePushPermission()
  if (currentOperation !== operation || readSession()?.token !== sessionToken || !nativePushEnabled()) return { ok: false, reason: cancelledError().message }
  if (status !== 'granted') return { ok: false, reason: status === 'denied' ? '通知权限已关闭，请在 iPhone 设置中允许诗序通知' : '尚未确认系统通知权限，请开启通知或稍后重试' }
  if (registeredSessionToken === sessionToken && deviceToken && permission === 'granted') {
    try {
      const key = await withTimeout(api.pushKey(sessionToken), 5_000, '读取推送服务状态超时')
      if (currentOperation !== operation || readSession()?.token !== sessionToken) return { ok: false, reason: cancelledError().message }
      serviceEnabled = key.nativeEnabled === true
    } catch { /* 保留最近一次服务状态；下次恢复或打开设置仍会重试。 */ }
    if (currentOperation !== operation || readSession()?.token !== sessionToken || !nativePushEnabled()) return { ok: false, reason: cancelledError().message }
    return { ok: true, devices: registeredDevices, nativeEnabled: serviceEnabled }
  }
  return registerNativePush(sessionToken, false)
}

/** First login requests optional system authorization; an explicit device opt-out is always respected. */
export async function initializeNativePush(sessionToken: string): Promise<NativePushResult> {
  if (!nativePushEnabled()) return { ok: false, reason: '本设备的通知已关闭' }
  if (registeredSessionToken === sessionToken && deviceToken && permission === 'granted') return restoreNativePush(sessionToken)
  return registerNativePush(sessionToken, true)
}

/** 关闭通知或退出账号时撤销本账号的设备绑定，随后撤销本机 APNs 登记。 */
export async function unregisterNativePush(sessionToken: string, disable = false): Promise<NativePushResult> {
  if (!nativePushAvailable()) return { ok: true }
  // An old logout may be delivered after a new login. It must not disable that
  // account's preference, clear its token, or enqueue a device-wide unregister.
  if (currentOperation && currentOperation.sessionToken !== sessionToken) return { ok: true }
  if (activeSessionToken && activeSessionToken !== sessionToken) return { ok: true }
  if (disable) setEnabled(false)
  const token = deviceToken
  const operation = beginOperation(sessionToken)
  activeSessionToken = null
  registeredSessionToken = null
  registeredDevices = 0
  reportProgress('')
  let error: string | undefined
  let devices: number | undefined
  // Queue native cleanup immediately, before waiting for any HTTP request.
  const nativeCleanup = withTimeout(mutateNative(async () => {
    if (currentOperation !== operation) return
    await PushNotifications.unregister()
  }), 5_000, '设备取消推送未响应，请完全关闭并重新打开诗序后重试').catch(caught => { error ??= caught instanceof Error ? caught.message : '设备取消推送失败' })
  const serverCleanup = (async () => { if (token) {
    try {
      const request = removeServerBinding(operation, token)
      const result = await withTimeout(request, 5_000, '服务器取消推送超时')
      devices = result.nativeDevices
    } catch (caught) { error = caught instanceof Error ? caught.message : '服务器取消推送失败' }
  } })()
  await Promise.all([nativeCleanup, serverCleanup])
  if (!error && currentOperation === operation) deviceToken = null
  return error ? { ok: false, reason: error } : { ok: true, devices }
}
